import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux'
import { bindActionCreators } from "redux";

import * as Actions from "actions";
import MockProductList from "components/elements/main/MockProductList";
import UnifiedPullToRefresh from "components/elements/main/UnifiedPullToRefresh";

import { getLocalDate, getURI } from "helpers/utils";
import { 
    Box, 
    Card, 
    Container, 
    Typography, 
    Grid,
    CircularProgress,
    IconButton,
    Pagination,
    Stack
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { CURRENCY, DATE_FORMAT } from "config/constants";
import PropTypes from "prop-types";

const UserOrderList = (props) => {
    const {
        isLoading, orderlistItems, symbol, history, bootupInfo, userMainGetOrderlistProducts, match, pagination
    } = props;

    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        userMainGetOrderlistProducts({ page: currentPage});
    }, [userMainGetOrderlistProducts, currentPage]);

    const handleRefresh = () => {
        userMainGetOrderlistProducts({ page: currentPage});
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderEmpty = () => (
        <Box textAlign="center" py={6}>
            <Typography variant="h6" color="text.secondary">
                No orders yet
            </Typography>
        </Box>
    );

    return (
        <UnifiedPullToRefresh
            onRefresh={handleRefresh}
            isRefreshing={isLoading}
            sx={{ py: 3 }}
        >
            <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
                <Box sx={{ mb: { xs: 2, sm: 4 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" fontWeight={600}>
                        My Orders
                    </Typography>
                    <IconButton onClick={() => userMainGetOrderlistProducts()} disabled={isLoading}>
                        {isLoading ? <CircularProgress size={24} /> : <Refresh />}
                    </IconButton>
                </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" py={6}>
                    <MockProductList />
                </Box>
            ) : orderlistItems.length === 0 ? (
                renderEmpty()
            ) : (
                <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    {/* Table Header - Hidden on mobile */}
                    <Box 
                        sx={{ 
                            background: "rgba(0, 0, 0, 0.02)",
                            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                            px: 3,
                            py: 2,
                            display: { xs: 'none', md: 'block' }
                        }}
                    >
                        <Grid container alignItems="center">
                            <Grid item xs={3}>
                                <Typography variant="body1" fontWeight={600} color="text.secondary">
                                    Order ID
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="body1" fontWeight={600} color="text.secondary">
                                    Date
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body1" fontWeight={600} color="text.secondary">
                                    Shop
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="body1" fontWeight={600} color="text.secondary">
                                    Status
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    {orderlistItems.map((order, index) => (
                        <Box
                            key={`${index + 1}`}
                            onClick={() => history.push(getURI(process.env.REACT_APP_TYPE, "order", "", "", order._id))}
                            sx={{
                                "&:hover": { 
                                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                                    '& .order-id': {
                                        color: 'primary.dark'
                                    }
                                },
                                cursor: "pointer",
                                transition: 'all 0.2s ease',
                                borderBottom: index < orderlistItems.length - 1 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none'
                            }}
                        >
                            {/* Desktop Layout */}
                            <Grid 
                                container 
                                alignItems="center" 
                                px={3} 
                                py={2.5}
                                sx={{ display: { xs: 'none', md: 'flex' } }}
                            >
                                <Grid item xs={3}>
                                    <Typography 
                                        variant="body1"
                                        className="order-id"
                                        sx={{ 
                                            color: 'primary.main',
                                            fontWeight: 600,
                                            transition: 'color 0.2s ease',
                                            fontSize: '1rem'
                                        }}
                                    >
                                        #{order.order_id}
                                    </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                                        {getLocalDate(order.created_at, DATE_FORMAT)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography 
                                        variant="body1"
                                        sx={{ 
                                            fontWeight: 500,
                                            color: 'text.primary',
                                            fontSize: '0.95rem'
                                        }}
                                    >
                                        {order.shop ? order.shop.title : 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Box
                                        sx={{
                                            display: 'inline-block',
                                            px: 2,
                                            py: 0.75,
                                            borderRadius: 1.5,
                                            backgroundColor: 
                                                order.status === "pending"
                                                    ? "warning.lighter"
                                                    : order.status === "paid"
                                                        ? "success.lighter"
                                                        : "action.hover",
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color:
                                                    order.status === "pending"
                                                        ? "warning.main"
                                                        : order.status === "paid"
                                                            ? "success.main"
                                                            : "text.secondary",
                                                fontWeight: 600,
                                                textTransform: 'capitalize',
                                                fontSize: '0.95rem'
                                            }}
                                        >
                                            {order.status || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Mobile Layout - Card Style */}
                            <Box 
                                sx={{ 
                                    display: { xs: 'block', md: 'none' },
                                    px: 2,
                                    py: 2
                                }}
                            >
                                <Grid container spacing={1.5}>
                                    {/* Row 1: Order ID and Status */}
                                    <Grid item xs={6}>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ display: 'block', mb: 0.5 }}
                                        >
                                            Order ID
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            className="order-id"
                                            sx={{ 
                                                color: 'primary.main',
                                                fontWeight: 600,
                                                transition: 'color 0.2s ease'
                                            }}
                                        >
                                            #{order.order_id}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ display: 'block', mb: 0.5 }}
                                        >
                                            Status
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'inline-block',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1,
                                                backgroundColor: 
                                                    order.status === "pending"
                                                        ? "warning.lighter"
                                                        : order.status === "paid"
                                                            ? "success.lighter"
                                                            : "action.hover",
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color:
                                                        order.status === "pending"
                                                            ? "warning.main"
                                                            : order.status === "paid"
                                                                ? "success.main"
                                                                : "text.secondary",
                                                    fontWeight: 600,
                                                    textTransform: 'capitalize'
                                                }}
                                            >
                                                {order.status || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Row 2: Date and Shop */}
                                    <Grid item xs={6}>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ display: 'block', mb: 0.5 }}
                                        >
                                            Date
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                            {getLocalDate(order.created_at, DATE_FORMAT)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ display: 'block', mb: 0.5 }}
                                        >
                                            Shop
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            fontWeight={600} 
                                            color="text.primary"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {order.shop ? order.shop.title : 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    ))}
                </Card>
            )}

            {/* Pagination */}
            {!isLoading && orderlistItems.length > 0 && pagination && pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                    <Stack spacing={2}>
                        <Pagination 
                            count={pagination.totalPages} 
                            page={currentPage} 
                            onChange={handlePageChange}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                        />
                        <Typography variant="body2" color="text.secondary" align="center">
                            Showing {((currentPage - 1) * pagination.limit) + 1} - {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} orders
                        </Typography>
                    </Stack>
                </Box>
            )}
            </Container>
        </UnifiedPullToRefresh>
    );
};

UserOrderList.defaultProps = {
    isLoading: false,
    symbol: CURRENCY,
};

UserOrderList.propTypes = {
    isLoading: PropTypes.bool,
    orderlistItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    symbol: PropTypes.string,
    history: PropTypes.objectOf(PropTypes.any).isRequired,
    bootupInfo: PropTypes.objectOf(PropTypes.any).isRequired,
    userMainGetOrderlistProducts: PropTypes.func.isRequired,
    pagination: PropTypes.object,
};

const userOrderList = connect(
    ({ userMainOrders, bootupInfo }) => ({
        bootupInfo: bootupInfo,
        reqStatus: userMainOrders.reqStatus,
        isLoading: userMainOrders.isLoading,
        serviceError: userMainOrders.serviceError,
        orderlistItems: userMainOrders.orderlist,
        symbol: bootupInfo.symbol,
        pagination: userMainOrders.pagination
    }),
    (dispatch) => bindActionCreators({ ...Actions }, dispatch)
)(UserOrderList);

export { userOrderList as default };
